const OAUTH_MESSAGE_TYPE = 'kim:helpscout-oauth';

interface HelpScoutOAuthMessage {
	type: typeof OAUTH_MESSAGE_TYPE;
	status: 'connected' | 'failed';
	errorCode?: string;
}

interface HelpScoutRequestErrorData {
	errorCode?: string;
	message?: string;
}

interface HelpScoutRequestError {
	response?: {
		status?: number;
		data?: HelpScoutRequestErrorData;
	};
}

const getRequestErrorData = (error: unknown): HelpScoutRequestErrorData =>
	(error as HelpScoutRequestError | undefined)?.response?.data ?? {};

const isOAuthMessageData = (value: unknown): value is HelpScoutOAuthMessage => {
	if (!value || typeof value !== 'object') return false;
	const message = value as Record<string, unknown>;
	return (
		message.type === OAUTH_MESSAGE_TYPE &&
		(message.status === 'connected' || message.status === 'failed') &&
		(message.errorCode === undefined || typeof message.errorCode === 'string')
	);
};

export const isHelpScoutOAuthMessage = (
	event: MessageEvent<unknown>,
	popup: Window | null,
	machineOrigin: string
): event is MessageEvent<HelpScoutOAuthMessage> => {
	return popup !== null && event.source === popup && event.origin === machineOrigin && isOAuthMessageData(event.data);
};

export const getHelpScoutErrorMessage = (errorCode?: string): string => {
	switch (errorCode) {
		case 'authorization_denied':
		case 'access_denied':
			return 'Authorization was denied. Authorize again when you are ready.';
		case 'state_expired':
		case 'invalid_state':
			return 'This authorization attempt expired. Authorize again to continue.';
		case 'popup_blocked':
			return 'Your browser blocked the authorization window. Allow popups and try again.';
		case 'popup_closed':
			return 'The authorization window closed before the connection finished.';
		case 'webhook_validation_failed':
			return 'Automatic webhook setup did not complete.';
		case 'dedicated_user_required':
			return 'Help Scout could not create the dedicated Kim AI reply identity. Check your Help Scout plan and permissions, then authorize again.';
		case 'dedicated_user_unavailable':
			return 'The dedicated Kim AI reply identity is unavailable. Kim is using the authorized Help Scout owner for replies; reconnect to retry setup.';
		default:
			return 'Kim couldn’t finish the connection. Authorize again to continue.';
	}
};

export const getHelpScoutRequestErrorMessage = (error: unknown): string => {
	const { errorCode, message } = getRequestErrorData(error);
	if (errorCode) return getHelpScoutErrorMessage(errorCode);
	const safeMessage = message?.trim();
	if (safeMessage && safeMessage.length <= 240) return safeMessage;
	return getHelpScoutErrorMessage();
};

export const logHelpScoutRequestFailure = (operation: 'authorize' | 'status', error: unknown): void => {
	const requestError = error as HelpScoutRequestError | undefined;
	console.error('[Help Scout] Request failed', {
		operation,
		status: requestError?.response?.status,
		errorCode: getRequestErrorData(error).errorCode
	});
};
